import { useRef } from 'react';
import { addMonths } from 'date-fns';
import { useDateNow, useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { breakpoints } from 'styles';
import { ModalButtonWithForm } from 'components/common';
import { Datepicker, FormGroup } from 'components/common/form';

import type { FormApi } from 'informed';
import type { CourseCompletion } from 'shared-types';

type FormValues = {
  completionDate: number;
  startedAtDate: number | null;
};

interface Props {
  isDropdown?: boolean;
  completion?: Pick<
    CourseCompletion,
    'id' | 'completionDate' | 'startedAtDate'
  >;
  updateUsers: () => Promise<void>;
  userId: number;
}

const FollowUsersEditCompletion = ({
  completion,
  isDropdown,
  updateUsers,
  userId
}: Props) => {
  const { dateNow } = useDateNow();

  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { course }
  } = useReduxState();

  const formApi = useRef<FormApi<FormValues>>(undefined);

  const handleCompletionTimeChange = (date: number) => {
    if (!formApi.current) return;
    const { values } = formApi.current.getState();

    // move startedAtDate to same day if it's after completionDate
    if (values.startedAtDate && values.startedAtDate > date) {
      formApi.current.setValue('startedAtDate', date);
    }
  };

  const handleSubmit = async (values: FormValues) => {
    try {
      // handle expirationDate
      const { completionExpiration, completionExpirationValue } = course;

      let expirationDate: number | null = null;
      if (completionExpiration && completionExpirationValue) {
        expirationDate = addMonths(
          values.completionDate,
          completionExpirationValue
        ).getTime();
      }

      let startedAtDate = completion.startedAtDate
        ? Date.parse(completion.startedAtDate as string)
        : values.completionDate;

      if (startedAtDate > values.completionDate) {
        startedAtDate = values.completionDate;
      }

      await api.post(
        `/admin/users/${userId}/course-completions/${completion.id}`,
        { completionDate: values.completionDate, expirationDate, startedAtDate }
      );
      await updateUsers();

      dispatch.addNotice({
        type: 'success',
        text: messages.completionManuallyCompletionEditSuccess
      });
    } catch (err) {
      await dispatch.handleErrorDispatch(err, err.message);
    }
  };

  const initialValues = completion
    ? {
        completionDate: Date.parse(completion.completionDate as string),
        startedAtDate: completion.startedAtDate
          ? Date.parse(completion.startedAtDate as string)
          : dateNow
      }
    : {};

  return (
    <ModalButtonWithForm
      buttonCategory="success"
      buttonIcon="edit"
      buttonIsDropdown={isDropdown}
      buttonSize="header"
      buttonText={messages.completionManuallyCompletionEdit}
      buttonTextHideBreakpoint={breakpoints.small - 1}
      initialValues={initialValues}
      modalActionText={messages.commonSave}
      modalAutoFocus={false}
      modalTitle={messages.completionManuallyCompletionEdit}
      modalWidth="medium"
      onSubmit={handleSubmit}
      setFormApi={(form: FormApi<FormValues>) => (formApi.current = form)}
      stopPropagation={true}
    >
      {({ values }: { values: FormValues }) => (
        <FormGroup label={messages.completionsCompletionDate}>
          <Datepicker
            field="completionDate"
            id="completion-date"
            onChange={handleCompletionTimeChange}
            style={{ width: 250 }}
          />
        </FormGroup>
      )}
    </ModalButtonWithForm>
  );
};

export default FollowUsersEditCompletion;
